/**
 * Where the user left a project: the mode, the floor, the plan viewport and the exact 3D
 * camera of both orbit and walk. Re-entering restores it, so a project opens on the view
 * it was closed on instead of the default framing. The frame the user actually saw is
 * stored beside it by `projectPreview`.
 *
 * The record is small and must never cost a scene save: a write that the browser refuses
 * is reported, not thrown, and an unreadable record is treated as "never left here".
 */
import { parseWallMode, type WallMode } from './wallMode';
import { normaliseViewRotation, parseWindowViewId } from './windowViews';
import type { Vec2 } from '../core/hp/types';

/** What the stored frame shows — also what the project card is labelled with. */
export type PreviewKind = 'plan' | 'orbit' | 'walk';

/** Plan metres per CSS pixel, the range the plan viewport is allowed to hold. */
export const PLAN_SCALE_LIMITS = { min: 0.0015, max: 0.25 };
/** The plan at 100 %: 100 CSS px a metre. */
export const PLAN_SCALE_100 = 0.01;
export const zoomPercent = (scale: number): number => scale > 0 ? Math.round(PLAN_SCALE_100 / scale * 100) : 100;

/** One three.js camera, flattened: enough to put it back exactly where it stood. */
export interface SavedCameraView {
  position: [number, number, number];
  quaternion: [number, number, number, number];
  target: [number, number, number];
  fov: number;
  /** The walk view's horizontal fov in degrees; the vertical one is re-derived per aspect. */
  walkFov: number | null;
  yaw: number;
  pitch: number;
}

/** Both 3D cameras, and which of them the user was looking through. */
export interface SavedCameras {
  mode: 'orbit' | 'walk';
  orbit: SavedCameraView | null;
  walk: SavedCameraView | null;
}

export interface ProjectView {
  mode: 'plan' | 'scene';
  levelIndex: number;
  planSurface: 'floor' | 'ceiling';
  plan: { scale: number; centre: Vec2 } | null;
  firstPerson: boolean;
  wallMode: WallMode;
  showCeiling: boolean;
  /** The view out of the windows and how far it is turned (`src/state/windowViews.ts`). A record from before it was
   * chosen has the default view. */
  windowView: string;
  windowViewRotation: number;
  cameras: SavedCameras | null;
  /** Set when a frame was stored with this record; drives the card's label. */
  previewKind: PreviewKind | null;
  savedAt: number;
}

const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const flag = (value: unknown): boolean => value === true;

function tuple<N extends number>(value: unknown, length: N): number[] | null {
  return Array.isArray(value) && value.length === length && value.every(finite) ? (value as number[]) : null;
}

function parseCamera(value: unknown): SavedCameraView | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const position = tuple(raw.position, 3), quaternion = tuple(raw.quaternion, 4), target = tuple(raw.target, 3);
  if (!position || !quaternion || !target || !finite(raw.fov) || !finite(raw.yaw) || !finite(raw.pitch)) return null;
  // A zero-length quaternion would rotate the camera into nothing.
  if (!Math.hypot(...quaternion)) return null;
  return {
    position: [position[0], position[1], position[2]],
    quaternion: [quaternion[0], quaternion[1], quaternion[2], quaternion[3]],
    target: [target[0], target[1], target[2]],
    fov: clamp(raw.fov, 5, 170),
    walkFov: finite(raw.walkFov) ? clamp(raw.walkFov, 5, 170) : null,
    yaw: raw.yaw, pitch: clamp(raw.pitch, -1.4, 1.4),
  };
}

function parseCameras(value: unknown): SavedCameras | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const orbit = parseCamera(raw.orbit), walk = parseCamera(raw.walk);
  if (!orbit && !walk) return null;
  const mode = raw.mode === 'walk' && walk ? 'walk' : 'orbit';
  return { mode: mode === 'orbit' && !orbit ? 'walk' : mode, orbit, walk };
}

/** A stored record, or null when it is absent, damaged or from an incompatible shape. */
export function parseProjectView(value: unknown): ProjectView | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  if (!finite(raw.savedAt)) return null;
  const plan = raw.plan && typeof raw.plan === 'object' ? raw.plan as Record<string, unknown> : null;
  const centre = plan?.centre && typeof plan.centre === 'object' ? plan.centre as Record<string, unknown> : null;
  const previewKind = raw.previewKind === 'plan' || raw.previewKind === 'orbit' || raw.previewKind === 'walk' ? raw.previewKind : null;
  return {
    mode: raw.mode === 'plan' ? 'plan' : 'scene',
    levelIndex: finite(raw.levelIndex) ? Math.max(0, Math.floor(raw.levelIndex)) : 0,
    planSurface: raw.planSurface === 'ceiling' ? 'ceiling' : 'floor',
    plan: plan && finite(plan.scale) && centre && finite(centre.x) && finite(centre.z)
      ? { scale: clamp(plan.scale, PLAN_SCALE_LIMITS.min, PLAN_SCALE_LIMITS.max), centre: { x: centre.x, z: centre.z } } : null,
    firstPerson: flag(raw.firstPerson),
    // Records from before the cutaway stored a `lowWalls` flag.
    wallMode: parseWallMode(raw.wallMode, raw.lowWalls),
    showCeiling: flag(raw.showCeiling),
    windowView: parseWindowViewId(raw.windowView),
    windowViewRotation: normaliseViewRotation(raw.windowViewRotation),
    cameras: parseCameras(raw.cameras),
    previewKind,
    savedAt: raw.savedAt,
  };
}

type ViewStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
const prefix = 'hp.view.';
export const projectViewKey = (projectId: string): string => prefix + projectId;

function browserStorage(): ViewStorage | null {
  try { return typeof localStorage === 'undefined' ? null : localStorage; } catch { return null; }
}

export function readProjectView(projectId: string, storage: ViewStorage | null = browserStorage()): ProjectView | null {
  if (!projectId || !storage) return null;
  try {
    const raw = storage.getItem(projectViewKey(projectId));
    return raw ? parseProjectView(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

/** Returns whether the browser accepted the record; a full quota is not an editor error. */
export function writeProjectView(projectId: string, view: ProjectView, storage: ViewStorage | null = browserStorage()): boolean {
  if (!projectId || !storage) return false;
  try { storage.setItem(projectViewKey(projectId), JSON.stringify(view)); return true; } catch { return false; }
}

export function removeProjectView(projectId: string, storage: ViewStorage | null = browserStorage()): void {
  if (!projectId || !storage) return;
  try { storage.removeItem(projectViewKey(projectId)); } catch { /* nothing left to clean up */ }
}

/** A copy of a project starts on the same view as its original. */
export function copyProjectView(fromId: string, toId: string, storage: ViewStorage | null = browserStorage()): ProjectView | null {
  const view = readProjectView(fromId, storage);
  if (view) writeProjectView(toId, view, storage);
  return view;
}
